import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { openWhatsApp } from "@/lib/whatsapp";
import { useAuth } from "@/context/AuthContext";

let cachedAccounts = null; // module-level cache (browser tab)
let cachedSignature = null;
let cachedAt = 0;

async function fetchAccounts(apiCall) {
  if (cachedAccounts && Date.now() - cachedAt < 60_000) {
    return { accounts: cachedAccounts, signature: cachedSignature };
  }
  try {
    const res = await apiCall("get", "/settings");
    cachedAccounts = res.data?.whatsapp_accounts || [];
    cachedSignature = res.data?.whatsapp_default_signature || "";
    cachedAt = Date.now();
  } catch {
    cachedAccounts = [];
    cachedSignature = "";
  }
  return { accounts: cachedAccounts, signature: cachedSignature };
}

export function refreshWhatsAppAccountsCache() {
  cachedAccounts = null;
  cachedAt = 0;
}

/**
 * Botão WhatsApp inteligente.
 * Se houver 0 ou 1 conta cadastrada → abre direto wa.me/<phone>.
 * Se houver 2+ contas → popover para o usuário escolher "Enviar como…".
 */
export default function WhatsAppButton({
  toPhone,
  message,
  buttonLabel,
  iconOnly = false,
  size = "icon",
  variant = "ghost",
  className = "",
  testId,
}) {
  const { apiCall } = useAuth();
  const [accounts, setAccounts] = useState(null);
  const [signature, setSignature] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchAccounts(apiCall).then(({ accounts: a, signature: s }) => {
      setAccounts(a);
      setSignature(s);
    });
  }, [apiCall]);

  const finalMessage = signature ? `${message || ""}${message ? "\n\n" : ""}${signature}` : message || "";

  const handleOpenWith = (label) => {
    if (!toPhone) {
      toast.error("Cliente sem telefone cadastrado");
      return;
    }
    const ok = openWhatsApp(toPhone, finalMessage);
    if (!ok) toast.error("Cliente sem telefone cadastrado");
    else if (label) toast.success(`Abrindo como ${label}. Confirme a conta no WhatsApp.`);
    setOpen(false);
  };

  // Single click flow
  if (accounts === null || accounts.length <= 1) {
    return (
      <Button
        type="button"
        size={size}
        variant={variant}
        className={className}
        onClick={() => handleOpenWith(accounts?.[0]?.label || null)}
        title="Abrir conversa no WhatsApp"
        data-testid={testId || "whatsapp-button"}
      >
        <MessageCircle className="h-4 w-4" strokeWidth={1.5} style={{ color: "#25D366" }} />
        {!iconOnly && buttonLabel && <span className="ml-1.5">{buttonLabel}</span>}
      </Button>
    );
  }

  // Multi-account popover
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size={size}
          variant={variant}
          className={className}
          title="Enviar como…"
          data-testid={testId || "whatsapp-button"}
        >
          <MessageCircle className="h-4 w-4" strokeWidth={1.5} style={{ color: "#25D366" }} />
          {!iconOnly && buttonLabel && <span className="ml-1.5">{buttonLabel}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" data-testid="whatsapp-account-popover">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1.5">Enviar como…</div>
        <div className="space-y-1">
          {accounts.map((acc, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleOpenWith(acc.label)}
              className="w-full text-left p-2 rounded hover:bg-secondary text-sm flex flex-col gap-0.5"
              data-testid={`whatsapp-account-option-${i}`}
            >
              <span className="font-medium text-foreground">{acc.label}</span>
              <span className="text-xs text-muted-foreground">{acc.phone}{acc.instagram_handle ? ` · ${acc.instagram_handle}` : ""}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => handleOpenWith(null)}
            className="w-full text-left p-2 rounded hover:bg-secondary text-xs text-muted-foreground border-t border-border mt-1 pt-2"
            data-testid="whatsapp-account-default"
          >
            Usar conta padrão do dispositivo
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
